/*************************************************************************
 *
 * c20g CONFIDENTIAL
 * __________________
 *
 *  [2007] - [2023] Counterpoint Consulting, Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Counterpoint Consulting Incorporated.
 * The intellectual and technical concepts contained
 * herein are proprietary to Counterpoint Consulting Incorporated
 * and its suppliers and may be covered by U.S. and Foreign Patents,
 * patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Counterpoint Consulting Incorporated.
 */

import _ from 'lodash';

import GridView from '@/views/GridView';
import StringCell from '@/backgrid/StringCell';
import ButtonCell from '@/backgrid/ButtonCell';
import PageableCollection from '@/backbone/PageableCollection';
import showModal from '@/util/showModal';
import showSuccessMessage from '@/util/ShowSuccessMessage';
import showErrorMessage from '@/util/showErrorMessage';
import generateCSV from '@/util/generateCSV';
import downloadCSV from '@/util/downloadCSV';
import SaveDeliverySchedules from '@/services/SaveDeliverySchedules';
import SpecialtyRequirementDeliveryScheduleModalView from '@/views/SpecialtyRequirementDeliveryScheduleModalView';
import HorizontalAddressCell from '@/views/HorizontalAddressCell.js/HorizontalAddressCell';

const SpecialtyRequirementDeliveryScheduleGridView = GridView.extend({
  className: 'specialty-requirement-delivery-schedule-grid-view horizontal-scroll',

  childEvents: {
    'next:clicked': 'onNextClicked',
    'previous:clicked': 'onPreviousClicked'
  },

  async initialize() {
    const self = this;
    const columns = [
      {
        name: 'lineNumber',
        label: '#',
        cell: StringCell,
        headerCell: 'custom',
        width: 10,
        editable: false,
        downloadable: true,
        direction: 'ascending'
      },
      {
        name: 'customDescription',
        label: 'Description',
        cell: StringCell,
        headerCell: 'custom',
        width: 30,
        editable: false,
        downloadable: true
      },
      {
        name: 'commandName',
        label: 'Organization/Unit',
        cell: StringCell,
        headerCell: 'custom',
        width: 20,
        editable: false,
        renderable: false,
        downloadable: true
      },
      {
        name: 'branchName',
        label: 'Branch of Service',
        cell: StringCell,
        headerCell: 'custom',
        width: 15,
        editable: false,
        renderable: false,
        downloadable: false
      },
      {
        name: 'commandEchelon',
        label: 'Command/Echelon',
        cell: StringCell,
        headerCell: 'custom',
        width: 20,
        editable: false,
        renderable: false,
        downloadable: true
      },
      {
        name: 'address',
        label: 'Address',
        cell: HorizontalAddressCell,
        headerCell: 'custom',
        width: 35,
        editable: false,
        sortable: true,
        downloadable: false
      },
      {
        name: 'completeAddress1',
        label:
          'Complete Address as Follows: 1st Street Address/2nd Building & Floor Number/3rd Room & Pole Number (No Commas or Punctuation)',
        cell: StringCell,
        headerCell: 'custom',
        width: 20,
        renderable: false,
        downloadable: true
      },
      {
        name: 'completeAddress2',
        label: 'Complete Address as Follows: 1st City/2nd State/3rd ZIP',
        cell: StringCell,
        headerCell: 'custom',
        width: 20,
        renderable: false,
        downloadable: true
      },
      {
        name: 'buildingFloorNumber',
        label: 'Building',
        cell: StringCell,
        headerCell: 'custom',
        width: 15,
        renderable: false,
        downloadable: true
      },
      {
        name: 'floorNumber',
        label: 'Floor Number',
        cell: StringCell,
        headerCell: 'custom',
        width: 15,
        renderable: false,
        downloadable: true
      },
      {
        name: 'roomNumber',
        label: 'Room Number',
        cell: StringCell,
        headerCell: 'custom',
        width: 15,
        renderable: false,
        downloadable: true
      },
      {
        name: 'poleNumber',
        label: 'Pole Number',
        cell: StringCell,
        headerCell: 'custom',
        width: 15,
        renderable: false,
        downloadable: true
      },
      {
        name: 'suite',
        label: 'Suite',
        cell: StringCell,
        headerCell: 'custom',
        width: 15,
        renderable: false,
        downloadable: true
      },
      {
        name: 'addressLine1',
        label: 'Address 1',
        cell: StringCell,
        headerCell: 'custom',
        width: 15,
        renderable: false,
        downloadable: true
      },
      {
        name: 'addressLine2',
        label: 'Address 2',
        cell: StringCell,
        headerCell: 'custom',
        width: 15,
        renderable: false,
        downloadable: true
      },
      {
        name: 'city',
        label: 'City/Base',
        cell: StringCell,
        headerCell: 'custom',
        width: 15,
        renderable: false,
        downloadable: true
      },
      {
        name: 'regionCode',
        label: 'State',
        cell: StringCell,
        headerCell: 'custom',
        width: 15,
        renderable: false,
        downloadable: true
      },
      {
        name: 'zip',
        label: 'Zip',
        cell: StringCell,
        headerCell: 'custom',
        width: 15,
        renderable: false,
        downloadable: true
      },
      {
        name: 'deliveryPOCName',
        label: 'Delivery Contact Name',
        cell: StringCell,
        headerCell: 'custom',
        width: 15,
        editable: false,
        downloadable: true
      },
      {
        name: 'deliveryPOCEmail',
        label: 'Delivery Contact Email',
        cell: StringCell,
        headerCell: 'custom',
        width: 15,
        editable: false,
        renderable: false,
        downloadable: true
      },
      {
        name: 'deliveryPOCPhone',
        label: 'Delivery Contact Phone',
        cell: StringCell,
        headerCell: 'custom',
        width: 15,
        editable: false,
        renderable: false,
        downloadable: true
      },
      {
        name: 'itPOCName',
        label: 'IT Contact Name',
        cell: StringCell,
        headerCell: 'custom',
        width: 15,
        editable: false,
        renderable: !!this.options.itPOCExists,
        downloadable: !!this.options.itPOCExists
      },
      {
        name: 'itPOCEmail',
        label: 'IT Contact Email',
        cell: StringCell,
        headerCell: 'custom',
        width: 15,
        editable: false,
        renderable: false,
        downloadable: true
      },
      {
        name: 'itPOCPhone',
        label: 'IT Contact Phone',
        cell: StringCell,
        headerCell: 'custom',
        width: 15,
        editable: false,
        renderable: false,
        downloadable: true
      },
      {
        name: 'notes',
        label: 'Notes/Message',
        cell: StringCell,
        headerCell: 'custom',
        width: 15,
        renderable: false,
        downloadable: true
      },
      {
        name: 'editViewButton',
        label: '',
        width: 7,
        headerCell: 'custom',
        cell: ButtonCell.extend({
          name() {
            if (self.options.editable) {
              return 'Edit';
            }
            return 'View';
          },
          onClick(e, model) {
            self.options.editable ? self.onClickEdit(e, model) : self.onClickView(e, model);
          },
          buttonClass: 'btn-sm btn-outline-primary text-center'
        }),
        editable: false,
        sortable: false
      }
    ];

    this.options.columns = columns;
    this.options.columns,
      (this.options.collection = new PageableCollection(this.model.get('schedules'), {
        pageSize: 50
      }));
    this.options.emptyText = 'No Devices Selected';
    //this.options.width = '3000px';
    GridView.prototype.initialize?.apply(this, this.options);
  },

  openPreviousItem(modalData) {
    let e;
    const newModel = this.options.collection.fullCollection.models[modalData.nextIndex];
    if (this.options.editable) {
      this.onClickEdit(e, newModel);
    } else {
      this.onClickView(e, newModel);
    }
  },

  openNextItem(modalData) {
    let e;
    const newModel = this.options.collection.fullCollection.models[modalData.nextIndex];
    if (this.options.editable) {
      this.onClickEdit(e, newModel);
    } else {
      this.onClickView(e, newModel);
    }
  },

  async saveRowItem(modalData, model) {
    // Manually find region, additional service, and training names from lookup data
    const region = _.find(this.model.get('regions'), (x) => {
      return x.id === modalData.formData.address.regionId;
    });
    modalData.formData.address.regionName = region?.name;
    modalData.formData.address.regionCode = region?.code;

    const service = _.find(this.model.get('additionalServices'), (x) => {
      return x.id === modalData.formData.additionalService;
    });
    modalData.formData.additionalServiceText = service?.name;

    const training = _.find(this.model.get('trainingTypes'), (x) => {
      return x.id === modalData.formData.trainingType;
    });
    modalData.formData.trainingTypeText = training?.name;

    // Set row model data in the UI
    if (modalData.formData.address.zip) {
      modalData.formData.address.isConus = '1';
    }
    this.cleanFormData(modalData.formData);
    model.set('address', modalData.formData.address);
    model.set(modalData.formData.address);

    model.set('commandEchelon', modalData.formData.commandEchelon);
    model.set('deliveryPOCName', modalData.formData.deliveryPOCName);
    model.set('deliveryPOCEmail', modalData.formData.deliveryPOCEmail);
    model.set('deliveryPOCPhone', modalData.formData.deliveryPOCPhone);
    const deliveryNames = model.get('deliveryPOCName').split(' ');
    const deliveryPOCGivenNames = deliveryNames
      .slice(0, deliveryNames.length <= 2 ? 1 : deliveryNames.length - 1)
      .join(' ');
    const deliveryPOCLastName = deliveryNames
      .slice(deliveryNames.length > 1 ? deliveryNames.length - 1 : deliveryNames.length)
      .join(' ');
    model.set('deliveryPOCFirstName', deliveryPOCGivenNames);
    model.set('deliveryPOCLastName', deliveryPOCLastName);

    if (this.options.itPOCExists) {
      model.set('itPOCName', modalData.formData.itPOCName);
      model.set('itPOCEmail', modalData.formData.itPOCEmail);
      model.set('itPOCPhone', modalData.formData.itPOCPhone);
      const itNames = model.get('itPOCName').split(' ');
      const itPOCGivenNames = itNames.slice(0, itNames.length <= 2 ? 1 : itNames.length - 1).join(' ');
      const itPOCLastName = itNames.slice(itNames.length > 1 ? itNames.length - 1 : itNames.length).join(' ');
      model.set('itPOCFirstName', itPOCGivenNames);
      model.set('itPOCLastName', itPOCLastName);
    }

    const index = _.findIndex(this.model.get('schedules'), ['id', model.get('id')]);
    this.model.get('schedules')[index] = model.toJSON();
    const deliverySchedules = [model.toJSON()];

    try {
      await SaveDeliverySchedules(this.options.objectID, this.options.objectTypeCode, deliverySchedules);

      showSuccessMessage({
        code: 'saveSuccess',
        title: 'Save Success',
        message: 'Delivery Schedule Saved.',
        hideAfter: 1
      });
    } catch (err) {
      // Ignore
    }
  },

  async onClickEdit(e, model) {
    if (e?.type) {
      e.preventDefault();
    }
    model.set('regions', this.model.get('regions'));
    model.set('commandPOCs', this.model.get('commandPOCs'));

    await showModal({
      title: 'Delivery Schedule Details',
      view: new SpecialtyRequirementDeliveryScheduleModalView({
        isReadOnly: false,
        itPOCExists: !!this.options.itPOCExists,
        isPriceVisible: !!this.options.isPriceVisible,
        model
      }),
      dialogClasses: 'modal-lg'
    }).then((modalData) => {
      _.each(Object.keys(modalData.formData), (key) => {
        model.set(key, modalData.formData[key]);
      });

      if (modalData.button === 'prev') {
        this.saveRowItem(modalData, model);
        this.openPreviousItem(modalData);
      } else if (modalData.button === 'next') {
        this.saveRowItem(modalData, model);
        this.openNextItem(modalData);
      } else if (modalData.button === 'save') {
        this.saveRowItem(modalData, model);
      }
    });
  },

  async onClickView(e, model) {
    if (e?.type) {
      e.preventDefault();
    }
    model.set('regions', this.model.get('regions'));

    const modalData = await showModal({
      title: 'Delivery Schedule Details',
      view: new SpecialtyRequirementDeliveryScheduleModalView({
        isReadOnly: true,
        itPOCExists: !!this.options.itPOCExists,
        isPriceVisible: !!this.options.isPriceVisible,
        model
      }),
      dialogClasses: 'modal-lg'
    });
    if (modalData.button === 'prev') {
      this.openPreviousItem(modalData);
    } else if (modalData.button === 'next') {
      this.openNextItem(modalData);
    }
  },

  getCSVColumns() {
    return this.options.columns.toJSON()
      .filter(column => column.downloadable && column.label)
      .map(({ name, label }) => ({ name, label }));
  },

  async downloadTemplateCSV() {
    const columnsToInclude = this.getCSVColumns();
    const csvContent = await generateCSV([], columnsToInclude);
    const fileName = `${this.options.objectTypeCode.toLowerCase()}_${this.options.objectID}_delivery_schedule_template`;
    await downloadCSV(csvContent, fileName);
  },

  async generateAndDownloadCSV() {
    try {
      const columnsToInclude = this.getCSVColumns();

      const data = this.options.collection.fullCollection.toJSON();
      _.each(data, (row) => {
        Object.assign(row, row.address);
        row.completeAddress1 = [
          row.addressLine1,
          row.addressLine2,
          row.buildingFloorNumber,
          row.floorNumber,
          row.roomPoleNumber
        ]
          .filter((x) => x)
          .join(' ');
        row.completeAddress2 = [row.city, row.regionCode, row.zip].filter((x) => x).join(' ');
      });

      const csvContent = await generateCSV(data, columnsToInclude);
      const fileName = `${this.options.objectTypeCode.toLowerCase()}_${this.options.objectID}_delivery_schedule`;
      await downloadCSV(csvContent, fileName);
    } catch (err) {
      showErrorMessage({
        errorCode: 'downloadError',
        errorTitle: 'Download Error',
        errorMessage: 'And error occurred while downloading delivery schedule CSV.'
      });
    }
  },

  cleanFormData(data) {
    const self = this;
    const address = data.address;
    Object.keys(address).forEach((key) => {
      address[key] = self.removePunctuation(address[key]);
    });
    data.deliveryPOCName = self.removePunctuation(data.deliveryPOCName);
    data.itPOCName = self.removePunctuation(data.itPOCName);
    data.commandEchelon = self.removePunctuation(data.commandEchelon);
  },

  removePunctuation(val) {
    if (!val) return;
    return val.replace(/[!"#$%&'()*+,-./:;<=>?@[\]^_`{|}~]/g, '');
  }
});

export default SpecialtyRequirementDeliveryScheduleGridView;
